// Shared types for the resource import wizard (Settings → Import → Resources).
import type { BaseStatusValue } from '@my/directory-api';

/**
 * Resource types the importer supports. A deliberate subset of
 * `ResourceTypeValue` — `Other` carries no distinguishing fields, so there's
 * nothing useful to bulk-map onto it.
 */
export type ImportResourceType = 'Driver' | 'Truck' | 'Rigid' | 'Trailer';

export const IMPORT_RESOURCE_TYPES: readonly ImportResourceType[] = [
  'Driver',
  'Truck',
  'Rigid',
  'Trailer',
] as const;

/** Wizard step machine — resource type is picked first, since it drives the mapping. */
export type WizardStep = 'type' | 'upload' | 'map' | 'preview' | 'commit';

/** Target fields a source column can map to. `status` is a batch default, not a column. */
export type ResourceField =
  | 'name'
  | 'displayName'
  | 'reference'
  | 'mobileNumber'
  | 'email'
  | 'equipmentType';

export interface ResourceFieldDef {
  id: ResourceField;
  label: string;
  required: boolean;
  hint?: string;
}

// ── Per-type field shape ─────────────────────────────────────────────────────
// Mirrors the BE validator's type-conditional rules (and `ResourceEditor`):
// mobile → Driver/Truck/Rigid, email → Driver, equipment type → Trailer/Rigid.
// Shipping a field the type doesn't allow gets the row rejected, so the mapping
// UI only ever offers the applicable ones.

export const hasMobile = (t: ImportResourceType) => t === 'Driver' || t === 'Truck' || t === 'Rigid';
export const hasEmail = (t: ImportResourceType) => t === 'Driver';
export const hasEquipment = (t: ImportResourceType) => t === 'Trailer' || t === 'Rigid';

/** `reference` means something different per type — same labels as the Resources page. */
export const REFERENCE_LABEL: Record<ImportResourceType, string> = {
  Driver: 'Licence number',
  Truck: 'Registration',
  Rigid: 'Registration',
  Trailer: 'Trailer number',
};

export const TYPE_LABEL: Record<ImportResourceType, string> = {
  Driver: 'Driver',
  Truck: 'Truck',
  Rigid: 'Rigid',
  Trailer: 'Trailer',
};

export const TYPE_HINT: Record<ImportResourceType, string> = {
  Driver: 'Name, licence number, mobile and email.',
  Truck: 'Name, registration and mobile.',
  Rigid: 'Name, registration, mobile and equipment type.',
  Trailer: 'Name, trailer number and equipment type.',
};

/** The mappable fields for a resource type, in display order. */
export function fieldsForType(type: ImportResourceType): ResourceFieldDef[] {
  const fields: ResourceFieldDef[] = [
    { id: 'name', label: 'Name', required: true },
    {
      id: 'displayName',
      label: 'Display name',
      required: false,
      hint: 'Blank → uses Name',
    },
    { id: 'reference', label: REFERENCE_LABEL[type], required: false },
  ];
  if (hasEquipment(type)) {
    fields.push({
      id: 'equipmentType',
      label: 'Equipment type',
      required: false,
      hint: 'Matched by name against your equipment types',
    });
  }
  if (hasMobile(type)) {
    fields.push({ id: 'mobileNumber', label: 'Mobile number', required: false });
  }
  if (hasEmail(type)) {
    fields.push({ id: 'email', label: 'Email', required: false });
  }
  return fields;
}

/** Target field → source header. Absent key = unmapped (ignored). */
export type ResourceColumnMapping = Partial<Record<ResourceField, string>>;

/** Batch-level settings the operator picks once on the mapping step. */
export interface ResourceImportDefaults {
  status: BaseStatusValue;
}

export const DEFAULT_RESOURCE_IMPORT_DEFAULTS: ResourceImportDefaults = {
  status: 'Active',
};

// ── Draft (materialised row, edited in preview, committed per-row) ───────────

export type DraftStatus = 'pending' | 'creating' | 'created' | 'skipped' | 'failed';

/**
 * One import row after mapping + defaults are applied. Mutable in the preview
 * grid; carries its own commit state. `rowRef` is a stable React /
 * result-correlation key.
 */
export interface ResourceImportDraft {
  rowRef: string;
  type: ImportResourceType;
  name: string;
  displayName: string;
  reference: string;
  mobileNumber: string;
  email: string;
  /** Raw equipment-type text from the file — kept so the preview can show what didn't match. */
  equipmentTypeText: string;
  /** Resolved org equipment-type row id. '' = unmatched or not applicable. */
  organisationEquipmentTypeId: string;
  status: BaseStatusValue;
  // Commit state
  status_: DraftStatus;
  createdResourceId?: string;
  error?: string;
}
